/**
 * Event Types for Frontend
 *
 * These types mirror the backend event schema but only include
 * the fields needed for display in the UI.
 */

export type EventType =
  | 'FIRE_INCIDENT'
  | 'BURN_AREA'
  | 'WEATHER'
  | 'HOTSPOT'
  | 'AIRCRAFT'
  | 'TRANSPORT'
  | 'POWER_OUTAGE'
  | 'COMMUNITY_REPORT'
  | 'HEALTH';

export type SeverityLevel =
  | 'ADVICE'
  | 'WATCH_AND_ACT'
  | 'EMERGENCY'
  | 'NOT_APPLICABLE';

export type FireStatus =
  | 'OUT_OF_CONTROL'
  | 'BEING_CONTROLLED'
  | 'UNDER_CONTROL'
  | 'PATROL'
  | 'UNKNOWN';

/**
 * A single entry in the fire status history.
 * Records when the fire's status or severity changed.
 */
export interface StatusHistoryEntry {
  /** Previous status before the change */
  status: FireStatus;
  /** Previous severity before the change */
  severity?: SeverityLevel;
  /** ISO timestamp when this status was recorded */
  at: string;
}

/**
 * Fire incident data with status history tracking.
 */
export interface FireIncidentData {
  status: FireStatus;
  severity: SeverityLevel;
  sizeHectares?: number;
  fireType?: string;
  resourceCount?: number;
  agencies?: string[];
  evacuationInEffect?: boolean;
  evacuationAreas?: string;
  /** History of status changes - newest last */
  statusHistory?: StatusHistoryEntry[];
}

export interface GeoLocation {
  lat: number;
  lon: number;
  radiusKm?: number;
}

export interface UnifiedEvent {
  id: string;
  type: EventType;
  location: GeoLocation;
  region: string;
  observedAt: string;
  confidence: number;
  title: string;
  description?: string;
  data: Record<string, unknown>;
  _distanceKm?: number;
}

export interface EventsResponse {
  events: UnifiedEvent[];
  meta: {
    count: number;
    radiusKm: number;
    minutesBack: number;
    centerLat: number;
    centerLon: number;
  };
}

export interface SummaryResponse {
  summaryText: string;
  stats: {
    countByType: Record<string, number>;
    timeWindowMinutes: number;
    radiusKm: number;
  };
}

/**
 * Preset locations for the area selector.
 */
export interface AreaPreset {
  id?: string;
  name: string;
  lat: number;
  lon: number;
  defaultRadiusKm?: number;
}

/**
 * Event type metadata for display.
 */
export interface EventTypeInfo {
  type: EventType;
  label: string;
  icon: string;
  color: string;
}

export const EVENT_TYPE_INFO: Record<EventType, EventTypeInfo> = {
  FIRE_INCIDENT: {
    type: 'FIRE_INCIDENT',
    label: 'Fire Incident',
    icon: '🔥',
    color: '#dc2626',
  },
  BURN_AREA: {
    type: 'BURN_AREA',
    label: 'Burn Area',
    icon: '🔲',
    color: '#a8b5a0',
  },
  WEATHER: {
    type: 'WEATHER',
    label: 'Weather',
    icon: '🌤️',
    color: '#2563eb',
  },
  HOTSPOT: {
    type: 'HOTSPOT',
    label: 'Hotspot',
    icon: '🛰️',
    color: '#ea580c',
  },
  AIRCRAFT: {
    type: 'AIRCRAFT',
    label: 'Aircraft',
    icon: '✈️',
    color: '#7c3aed',
  },
  TRANSPORT: {
    type: 'TRANSPORT',
    label: 'Road Closure',
    icon: '🚧',
    color: '#ca8a04',
  },
  POWER_OUTAGE: {
    type: 'POWER_OUTAGE',
    label: 'Power Outage',
    icon: '⚡',
    color: '#64748b',
  },
  COMMUNITY_REPORT: {
    type: 'COMMUNITY_REPORT',
    label: 'Community Report',
    icon: '👥',
    color: '#0891b2',
  },
  HEALTH: {
    type: 'HEALTH',
    label: 'Health Advisory',
    icon: '💨',
    color: '#8b5cf6', // Purple - distinct from fire (red) and weather (blue)
  },
};

/**
 * Get the severity color for a fire incident.
 */
export function getSeverityColor(severity: SeverityLevel): string {
  switch (severity) {
    case 'EMERGENCY':
      return '#dc2626'; // Red
    case 'WATCH_AND_ACT':
      return '#f97316'; // Orange
    case 'ADVICE':
      return '#ffffff'; // White
    default:
      return '#6b7280'; // Gray
  }
}

/**
 * Get the severity icon for a fire incident.
 * Always returns fire icon - severity is shown via background color.
 */
export function getSeverityIcon(_severity: SeverityLevel): string {
  return '🔥'; // Always show fire icon
}

/**
 * Format relative time (e.g., "5 minutes ago").
 */
export function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hr ago`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
}

/**
 * Wind data from weather API.
 */
export interface WindData {
  latitude: number;
  longitude: number;
  wind_speed_kmh: number | null;
  wind_direction_degrees: number | null;
  wind_gusts_kmh: number | null;
  timestamp: string;
  source: string;
}

/**
 * Hourly wind forecast entry.
 */
export interface WindForecastEntry {
  time: string;
  wind_speed_kmh: number | null;
  wind_direction_degrees: number | null;
  wind_gusts_kmh: number | null;
}

/**
 * Wind forecast response from weather API.
 */
export interface WindForecastResponse {
  latitude: number;
  longitude: number;
  forecast: WindForecastEntry[];
  source: string;
}

/**
 * Convert wind direction degrees to cardinal direction.
 */
export function getWindDirection(degrees: number | null): string {
  if (degrees === null) return 'N/A';

  const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
                     'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  const index = Math.round(degrees / 22.5) % 16;
  return directions[index];
}

/**
 * Aircraft state from ADS-B tracking.
 */
export interface AircraftState {
  icao24: string;
  callsign: string | null;
  origin_country: string;
  latitude: number | null;
  longitude: number | null;
  altitude_m: number | null;
  velocity_ms: number | null;
  heading: number | null;
  vertical_rate: number | null;
  on_ground: boolean;
  category: number | null;
  is_helicopter: boolean;
  /** Whether aircraft is likely emergency/firefighting service */
  is_emergency?: boolean;
  // Aircraft metadata
  registration: string | null;
  manufacturer: string | null;
  model: string | null;
  operator: string | null;
  /** ICAO type designator (e.g. EC35, B412, C130) */
  type_code?: string | null;
  /** Seconds since last ADS-B message (for staleness detection) */
  seen?: number;
  /** Seconds since last position update */
  seen_pos?: number;
  /** Altitude in feet (pre-converted for display) */
  altitude_ft?: number | null;
  /** Ground speed in knots (pre-converted for display) */
  speed_kts?: number | null;
}

/**
 * Response from legacy aircraft tracking endpoint (OpenSky via API Gateway).
 * @deprecated Use AircraftCacheResponse instead
 */
export interface AircraftResponse {
  aircraft: AircraftState[];
  timestamp: string;
  bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  source: string;
  credits_info: string;
}

/**
 * Response from cached aircraft data (CloudFront → S3).
 * This is the primary aircraft data source — polled by a Lambda
 * every 5 seconds and served via CloudFront edge cache.
 */
export interface AircraftCacheResponse {
  aircraft: AircraftState[];
  timestamp: string;
  source: string;
  totalRaw: number;
  totalFiltered: number;
  serverTime: number | null;
  pollIntervalMs: number;
}

/**
 * News article from search results.
 */
export interface NewsArticle {
  title: string;
  url: string;
  source: string;
  published: string | null;
  snippet: string | null;
}

/**
 * Social intelligence response with news and social links.
 */
export interface SocialIntelResponse {
  news_articles: NewsArticle[];
  social_links: {
    twitter: string;
    facebook: string;
    reddit: string;
    youtube: string;
    abc_news: string;
    nine_news: string;
    google: string;
  };
  search_query: string;
  timestamp: string;
}

/**
 * A geotagged photo with extracted EXIF location data.
 */
export interface GeoPhoto {
  id: string;
  /** URL to the uploaded image */
  url: string;
  /** Original file name */
  fileName: string;
  /** GPS latitude from EXIF */
  lat: number;
  /** GPS longitude from EXIF */
  lon: number;
  /** Altitude in meters (if available) */
  altitude?: number;
  /** Date/time the photo was taken */
  takenAt?: string;
  /** Timestamp when added to the app */
  addedAt: string;
  /** Moderation status: pending, approved, rejected */
  status?: 'pending' | 'approved' | 'rejected';
}

// ============================================================================
// FIRE SPREAD PREDICTION TYPES
// ============================================================================

/**
 * Time horizons for fire spread predictions.
 */
export type SpreadTimeHorizon = '30min' | '1hr' | '2hr' | '4hr';

/**
 * Risk levels for spread zones.
 */
export type SpreadRiskLevel = 'extreme' | 'high' | 'moderate' | 'low';

/**
 * Fuel type classification (simplified for McArthur model).
 */
export type FuelType = 'grassland' | 'forest';

/**
 * A directional spread zone representing fire advance in one direction.
 */
export interface SpreadZone {
  /** Direction of spread in degrees from north (0-360) */
  direction: number;
  /** Expected spread distance at 1 hour in meters */
  distanceMeters: number;
  /** Width of the zone in degrees */
  spreadAngle: number;
  /** Probability of fire reaching this zone (0-1) */
  probability: number;
  /** Risk classification */
  riskLevel: SpreadRiskLevel;
  /** GeoJSON polygon for the zone */
  polygon: GeoJSON.Polygon;
}

/**
 * Environmental conditions used for spread calculation.
 */
export interface SpreadConditions {
  /** Wind speed in km/h */
  windSpeedKmh: number;
  /** Wind direction in degrees (meteorological - where wind is FROM) */
  windDirectionDeg: number;
  /** Fire danger rating level */
  fdrLevel: string;
  /** Forest Fire Danger Index */
  ffdi: number;
  /** Temperature in Celsius */
  temperature: number;
  /** Relative humidity percentage */
  humidity: number;
  /** Assumed fuel type */
  fuelType: FuelType;
}

/**
 * Terrain analysis for slope-driven spread.
 */
export interface TerrainAnalysis {
  /** Steepest upslope direction (degrees from north) */
  upslopeDirection: number;
  /** Slope angle in degrees */
  slopeAngle: number;
  /** Whether slope is significant enough to override wind (>10°) */
  isSignificant: boolean;
  /** Angle difference from wind direction */
  windAngleDiff: number;
}

/**
 * Rate of spread estimates in meters per hour.
 */
export interface SpreadRates {
  /** Head fire (downwind) spread rate */
  headMetersPerHour: number;
  /** Flank fire (perpendicular) spread rate */
  flankMetersPerHour: number;
  /** Back fire (upwind) spread rate */
  backMetersPerHour: number;
  /** Upslope spread rate (if terrain override active) */
  upslopeMetersPerHour?: number;
}

/**
 * Complete fire spread prediction for a single fire.
 */
export interface FireSpreadPrediction {
  /** Fire event ID */
  fireId: string;
  /** Fire location */
  fireLocation: GeoLocation;
  /** Fire title/name */
  fireTitle: string;
  /** When prediction was calculated */
  timestamp: string;

  /** Environmental conditions used */
  conditions: SpreadConditions;

  /** Terrain analysis */
  terrain?: TerrainAnalysis;

  /** Directional spread zones */
  zones: {
    /** Head fire zone (downwind - fastest) */
    head: SpreadZone;
    /** Left flank zone (90° counterclockwise from wind) */
    flankLeft: SpreadZone;
    /** Right flank zone (90° clockwise from wind) */
    flankRight: SpreadZone;
    /** Back fire zone (upwind - slowest) */
    back: SpreadZone;
    /** Upslope override zone (if terrain dominates) */
    upslope?: SpreadZone;
  };

  /** Time-based perimeter polygons */
  perimeters: Record<SpreadTimeHorizon, GeoJSON.Polygon>;

  /** Per-horizon wind bearings (direction fire is heading, degrees from north) */
  horizonWindBearings?: Record<SpreadTimeHorizon, number>;

  /** Rate of spread estimates */
  ros: SpreadRates;
}

/**
 * Colors for fire spread time horizons.
 */
export const SPREAD_TIME_COLORS: Record<SpreadTimeHorizon, { fill: string; border: string }> = {
  '30min': { fill: 'rgba(220, 38, 38, 0.4)', border: '#991b1b' },   // Red
  '1hr': { fill: 'rgba(249, 115, 22, 0.3)', border: '#c2410c' },    // Orange
  '2hr': { fill: 'rgba(234, 179, 8, 0.2)', border: '#a16207' },     // Yellow
  '4hr': { fill: 'rgba(59, 130, 246, 0.15)', border: '#1d4ed8' },   // Blue
};

/**
 * Display labels for time horizons.
 */
export const SPREAD_TIME_LABELS: Record<SpreadTimeHorizon, string> = {
  '30min': '30 minutes',
  '1hr': '1 hour',
  '2hr': '2 hours',
  '4hr': '4 hours',
};
